import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Package, Handshake, BadgeCheck, BadgeEuro, Zap, Star, Quote, Shield, ChevronRight, Sparkles } from "lucide-react";
import api from "../lib/api";
import { CompanyCard } from "../components/CompanyCard";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { ILE_DE_FRANCE, MAJOR_CITIES } from "../lib/regions";

export default function Home() {
  const [cities, setCities] = useState([]);
  const [deptStats, setDeptStats] = useState({});
  const [featured, setFeatured] = useState([]);
  const navigate = useNavigate();

  // Mini hero form
  const [departure, setDeparture] = useState("");
  const [arrival, setArrival] = useState("");
  const [movingDate, setMovingDate] = useState("");

  useEffect(() => {
    api.get("/companies/cities").then((r) => {
      const map = {};
      (r.data || []).forEach((c) => { map[c.slug] = c.count; });
      setCities(map);
    });
    api.get("/companies/departments").then((r) => {
      const map = {};
      (r.data || []).forEach((d) => { map[d.code] = d.count; });
      setDeptStats(map);
    });
      api.get("/companies", { params: { limit: 12 } }).then((r) =>
        setFeatured((r.data?.items || []).filter((c) => c.listing_status === "partner" || c.listing_status === "partner_verified").slice(0, 6))
      );
    document.title = "Déménageur Pro — Déménagez l'esprit tranquille";
  }, []);

  const startQuote = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (departure) params.set("dep", departure);
    if (arrival) params.set("arr", arrival);
    if (movingDate) params.set("date", movingDate);
    navigate(`/devis${params.toString() ? "?" + params.toString() : ""}`);
  };

  return (
    <div className="animate-fade-up">
      {/* ===== HERO ===== */}
      <section className="relative min-h-[680px] md:min-h-[760px] flex items-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1920&q=80"
          alt="Famille heureuse devant nouvelle maison"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="hero-overlay" />

        <div className="relative z-10 container mx-auto px-6 lg:px-10 py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-medium mb-8" data-testid="hero-badge">
            <Sparkles className="w-3.5 h-3.5 text-[#2BD391]" />
            Plus de 12 000 déménagements organisés
          </div>

          <h1 className="font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] mb-6 max-w-4xl mx-auto" style={{ color: 'white' }}>
            Profitez, on s'occupe<br/>
            de votre <span className="text-[#2BD391]">déménagement</span>
          </h1>
          <p className="text-white/90 text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            Recevez gratuitement plusieurs devis personnalisés des meilleures entreprises de déménagement en France.
            Service 100% gratuit et sans engagement.
          </p>

          {/* Quote widget */}
          <form onSubmit={startQuote} className="bg-white rounded-3xl shadow-2xl p-4 md:p-5 max-w-4xl mx-auto" data-testid="hero-quote-widget">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-4">
                <AddressAutocomplete
                  value={departure}
                  onChange={setDeparture}
                  placeholder="Adresse de départ"
                  className="input-clean !pl-12"
                  iconClassName="left-5"
                  testid="hero-departure"
                />
              </div>
              <div className="md:col-span-4">
                <AddressAutocomplete
                  value={arrival}
                  onChange={setArrival}
                  placeholder="Adresse d'arrivée"
                  className="input-clean !pl-12"
                  iconClassName="left-5"
                  testid="hero-arrival"
                />
              </div>
              <div className="md:col-span-2 relative">
                <Calendar className="w-4 h-4 text-[#2BD391] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
                <input
                  type="date"
                  value={movingDate}
                  onChange={(e) => setMovingDate(e.target.value)}
                  className="input-clean !pl-10 !pr-2 !text-sm appearance-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  data-testid="hero-date"
                />
              </div>
              <button type="submit" className="md:col-span-2 btn-primary w-full !rounded-full !text-xs !px-3 whitespace-nowrap" data-testid="hero-cta-btn">
                Je déménage
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">
              ✓ Gratuit · ✓ Sans engagement · ✓ Devis auprès d'entreprises partenaires
            </p>
          </form>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="eyebrow mb-3">Notre promesse</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl">Déménagez l'esprit tranquille</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              { icon: Handshake, t: "Un expert dédié", st: "à votre écoute", b: ["Interlocuteur unique et indépendant", "Suivi tout au long du déménagement", "Conseils personnalisés"] },
              { icon: BadgeEuro, t: "Des tarifs compétitifs", st: "et garantis", b: ["Accompagnement gratuit", "Mise en concurrence des prestataires", "Aucun surcoût le jour J"] },
              { icon: BadgeCheck, t: "Des déménageurs", st: "professionnels", b: ["Entreprises partenaires et référencées", "Solutions adaptées à vos besoins", "Suivi qualité systématique"] },
              { icon: Zap, t: "Transfert de tous", st: "vos contrats", b: ["Énergie, internet, assurance, courrier", "Optimisation de vos dépenses", "Gain de temps"] },
            ].map(({ icon: Icon, t, st, b }, i) => (
              <div key={i} className="feature-icon text-center group" data-testid={`feature-${i}`}>
                <div className="feature-icon-bg mx-auto mb-6">
                  <Icon className="w-10 h-10" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-1">{t}</h3>
                <p className="font-heading font-bold text-lg text-[#2C3E50] mb-4">{st}</p>
                <ul className="text-sm text-[#6B7280] space-y-1.5">
                  {b.map((x) => <li key={x}>{x}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-20 md:py-28 bg-[#F7F9FB]">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="eyebrow mb-3">Comment ça marche</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl">Votre déménagement<br/>en 4 étapes simples</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { n: "1", t: "Décrivez votre besoin", d: "Remplissez le formulaire en quelques minutes : trajet, volume, date." },
              { n: "2", t: "Recevez nos devis", d: "Notre réseau de déménageurs partenaires vous envoie des devis personnalisés." },
              { n: "3", t: "Comparez et choisissez", d: "Comparez les prix et les prestations pour choisir le déménageur qui vous convient." },
              { n: "4", t: "Laissez-vous déménager", d: "Profitez de votre déménagement, on s'occupe de tout, jusqu'à l'emménagement." },
            ].map((s, i) => (
              <div key={i} className="relative bg-white rounded-2xl p-7 border border-[#F0F2F5] hover:border-[#2BD391] hover:shadow-[0_12px_32px_-8px_rgba(43,211,145,0.18)] transition-all" data-testid={`step-${s.n}`}>
                <div className="w-12 h-12 bg-[#E8FBF3] text-[#2BD391] rounded-full flex items-center justify-center font-heading font-extrabold text-xl mb-5">
                  {s.n}
                </div>
                <h3 className="font-heading font-bold text-lg mb-2 text-[#2C3E50]">{s.t}</h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">{s.d}</p>
                {i < 3 && <ChevronRight className="hidden lg:block w-6 h-6 text-[#2BD391] absolute -right-4 top-12" />}
              </div>
            ))}
          </div>

          <div className="text-center mt-14">
            <Link to="/devis" className="btn-primary btn-large" data-testid="how-cta">
              Démarrer ma demande de devis <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FEATURED COMPANIES ===== */}
      {featured.length > 0 && (
        <section className="py-20 md:py-28 bg-white">
          <div className="container mx-auto px-6 lg:px-10">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
              <div>
                <div className="eyebrow mb-3">Nos recommandations</div>
                <h2 className="font-heading font-extrabold text-3xl md:text-5xl">Entreprises recommandées</h2>
                <p className="text-[#6B7280] mt-3 max-w-xl">Entreprises de déménagement que nous recommandons pour la qualité de leurs prestations et leur engagement à recevoir vos demandes de devis via Déménageur Pro.</p>
              </div>
              <Link to="/villes" className="btn-outline" data-testid="see-all-cities">
                Voir l'annuaire complet <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((c) => <CompanyCard key={c.id} company={c} />)}
            </div>
          </div>
        </section>
      )}

      {/* ===== CITIES / REGIONS ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="eyebrow mb-3">Couverture nationale</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl">Trouvez un déménageur près de chez vous</h2>
            <p className="text-[#6B7280] mt-3 max-w-xl mx-auto">Région Île-de-France, grandes villes, ou département : choisissez votre zone.</p>
          </div>

          {/* IDF Block */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-[#1A2A3A] to-[#2C3E50] text-white rounded-3xl p-7 md:p-9 mb-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-72 h-72 bg-[#2BD391]/15 rounded-full blur-3xl"></div>
              <div className="relative flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2BD391] text-white text-xs font-bold uppercase tracking-wider mb-3">
                    <Star className="w-3 h-3 fill-white" /> Région #1
                  </div>
                  <h3 className="font-heading font-extrabold text-3xl md:text-4xl text-white mb-1" style={{ color: 'white' }}>Île-de-France</h3>
                  <p className="text-white/80 text-sm">8 départements · {ILE_DE_FRANCE.reduce((s, d) => s + (deptStats[d.code] || 0), 0).toLocaleString("fr-FR")} déménageurs</p>
                </div>
                <Link to="/demenagement/ile-de-france" className="btn-ghost-white" data-testid="home-idf-cta">
                  Toute l'IDF <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {ILE_DE_FRANCE.map((d) => (
                <Link
                  key={d.code}
                  to={`/demenagement/ile-de-france/${['75','77','78','91','92','93','94','95'].includes(d.code) ? ({'75':'paris-75','77':'seine-et-marne-77','78':'yvelines-78','91':'essonne-91','92':'hauts-de-seine-92','93':'seine-saint-denis-93','94':'val-de-marne-94','95':'val-d-oise-95'})[d.code] : d.code}`}
                  data-testid={`home-idf-dept-${d.code}`}
                  className="group bg-[#F7F9FB] hover:bg-[#E8FBF3] border border-transparent hover:border-[#2BD391] rounded-2xl p-4 transition-all"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-heading font-extrabold text-[#1AA66C]">{d.code}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-[#6B7280] group-hover:text-[#2BD391] group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <div className="font-heading font-bold text-sm text-[#2C3E50]">{d.short}</div>
                  <div className="text-[10px] text-[#6B7280] mt-0.5">{deptStats[d.code] || 0} déménageurs</div>
                </Link>
              ))}
            </div>
          </div>

          {/* Major cities */}
          <div className="mb-10">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
              <h3 className="font-heading font-extrabold text-2xl md:text-3xl text-[#2C3E50]">Grandes villes</h3>
              <Link to="/villes" className="text-sm font-semibold text-[#2BD391] hover:underline">Toutes les régions →</Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {MAJOR_CITIES.filter((c) => c.slug !== "paris").slice(0, 12).map((c) => (
                <Link
                  key={c.slug}
                  to={`/demenageurs/${c.slug}`}
                  className="group flex items-center justify-between bg-[#F7F9FB] hover:bg-[#E8FBF3] border border-transparent hover:border-[#2BD391] rounded-2xl px-5 py-4 transition-all"
                  data-testid={`home-major-${c.slug}`}
                >
                  <div>
                    <div className="font-heading font-bold text-[#2C3E50] group-hover:text-[#1AA66C]">{c.name}</div>
                    <div className="text-xs text-[#6B7280] mt-0.5">
                      {cities[c.slug] ? `${cities[c.slug]} déménageurs` : `Dépt. ${c.department}`}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#2BD391] group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="relative bg-gradient-to-br from-[#2BD391] to-[#1AA66C] rounded-3xl p-10 md:p-16 overflow-hidden text-center">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxIiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMTUpIi8+PC9zdmc+')] opacity-30"></div>
            <div className="relative">
              <Shield className="w-12 h-12 text-white mx-auto mb-6" strokeWidth={1.5} />
              <h2 className="font-heading font-extrabold text-white text-3xl md:text-5xl tracking-tight mb-4 max-w-2xl mx-auto" style={{ color: 'white' }}>
                Prêt à recevoir vos<br/>premiers devis ?
              </h2>
              <p className="text-white/90 max-w-xl mx-auto mb-8">
                Un seul formulaire, plusieurs devis personnalisés. C'est gratuit, sans engagement, et vous gardez la main.
              </p>
              <Link to="/devis" className="inline-flex items-center gap-2 bg-white text-[#1AA66C] hover:bg-[#1A2A3A] hover:text-white rounded-full px-8 py-4 font-bold text-sm uppercase tracking-wider transition-all" data-testid="final-cta">
                Démarrer maintenant <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
